/**
 * L4: reveal deadlines (Quick Search rule 2: a revealed value leaves the screen after 30 s in the
 * detail view). A timer that HIDES a value needs no gesture, so this carries over from the Mac as-is.
 * Large Type uses the same deadline. State is a plain record of FieldRef → deadline (ms epoch);
 * every function returns a new record.
 */
import type { FieldRef } from "../contract.js";

export const REVEAL_MS = 30_000;

export type Revealed = Record<FieldRef, number>;

export function isRevealed(revealed: Revealed, ref: FieldRef, now: number): boolean {
  const deadline = revealed[ref];
  return deadline !== undefined && now < deadline;
}

/** Hide a live reveal; otherwise reveal for REVEAL_MS from now. */
export function toggleReveal(revealed: Revealed, ref: FieldRef, now: number): Revealed {
  const next = { ...revealed };
  if (isRevealed(revealed, ref, now)) delete next[ref];
  else next[ref] = now + REVEAL_MS;
  return next;
}

/** Reveal with a fresh deadline (Large Type), whether or not it was already revealed. */
export function revealFor(revealed: Revealed, ref: FieldRef, now: number): Revealed {
  return { ...revealed, [ref]: now + REVEAL_MS };
}

export function pruneExpired(revealed: Revealed, now: number): Revealed {
  const next: Revealed = {};
  for (const [ref, deadline] of Object.entries(revealed)) if (now < deadline) next[ref] = deadline;
  return next;
}

export function nextExpiry(revealed: Revealed): number | null {
  const all = Object.values(revealed);
  return all.length ? Math.min(...all) : null;
}
